import { useState } from "react";
import {
  EXPENSE_TYPE,
  INCOME_TYPE,
  type Category,
  normalizeType,
} from "../../common/commonConstant";
import useDeviceModeFlg from "../../common/useDeviceModeFlg";
import Spinner from "../../common/Spinner";
import { getExpenseRatioLevel } from "../../common/expenseRatioLevel";

type ChartType = "pie" | "bar";

const CHART_TYPE_OPTIONS: Array<{ value: ChartType; label: string }> = [
  { value: "pie", label: "円グラフ" },
  { value: "bar", label: "棒グラフ" },
];

interface ChartItem {
  label: string;
  amount: number;
  value: number;
}

interface BOP020W030TemplateProps {
  month: string;
  isLoading: boolean;
  results: any[];
  sortKey: string;
  sortDirection: "asc" | "desc";
  onMonthChange: (value: string) => void;
  onClear: () => void;
  onBack: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onDetailClick: (row: any) => void;
  onSort: (key: string) => void;
  showResults: boolean;
}

const BOP020W030Template = ({
  month,
  isLoading,
  results,
  sortKey,
  sortDirection,
  onMonthChange,
  onClear,
  onBack,
  onSubmit,
  onDetailClick,
  onSort,
  showResults,
}: BOP020W030TemplateProps) => {
  const deviceModeFlg = useDeviceModeFlg();
  const [activeLegend, setActiveLegend] = useState<"income" | "expense" | null>(
    null,
  );
  const [chartType, setChartType] = useState<ChartType>("pie");
  const chartColors = [
    "#2563eb",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#14b8a6",
    "#f97316",
  ];

  const getChartData = (type: Category) => {
    const aggregated: Record<string, { amount: number; value: number }> =
      results.reduce(
        (acc, row) => {
          const normalizedType = normalizeType(row.type);
          if (normalizedType !== type) return acc;
          const label = String(
            row.categorySmall || row.categoryLarge || "",
          ).trim();
          if (!label || label === "合計" || label === "差分") return acc;
          const amount = Number(row.amount) || 0;
          const value = Math.abs(amount);
          if (value === 0) return acc;

          if (!acc[label]) {
            acc[label] = { amount: 0, value: 0 };
          }
          acc[label].amount += amount;
          acc[label].value += value;
          return acc;
        },
        {} as Record<string, { amount: number; value: number }>,
      );

    return Object.entries(aggregated)
      .map(([label, data]) => ({ label, ...data }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
  };

  const incomeChartData = getChartData(INCOME_TYPE);
  const expenseChartData = getChartData(EXPENSE_TYPE);

  const buildPieSlices = (chartData: ChartItem[]) => {
    const chartTotal = chartData.reduce((sum, item) => sum + item.value, 0);
    let cumulativeRatio = 0;

    return chartData.map((item, index) => {
      const valueRatio = item.value / chartTotal;
      if (valueRatio <= 0) return null;

      const radius = 80;
      const center = 100;
      const fillColor = chartColors[index % chartColors.length];

      if (valueRatio >= 0.999999) {
        return (
          <circle
            key={item.label}
            cx={center}
            cy={center}
            r={radius}
            fill={fillColor}
          />
        );
      }

      const startAngle = cumulativeRatio * 2 * Math.PI - Math.PI / 2;
      cumulativeRatio += valueRatio;
      const endAngle = cumulativeRatio * 2 * Math.PI - Math.PI / 2;
      const x1 = center + radius * Math.cos(startAngle);
      const y1 = center + radius * Math.sin(startAngle);
      const x2 = center + radius * Math.cos(endAngle);
      const y2 = center + radius * Math.sin(endAngle);
      const largeArcFlag = valueRatio > 0.5 ? 1 : 0;
      const path = `
        M ${center} ${center}
        L ${x1} ${y1}
        A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}
        Z
      `;
      return <path key={item.label} d={path} fill={fillColor} />;
    });
  };

  const toggleLegend = (target: "income" | "expense") => {
    if (!deviceModeFlg) {
      return;
    }
    setActiveLegend((prev) => (prev === target ? null : target));
  };

  // 棒グラフ（PCは縦棒、SPは横棒）。項目名・金額を棒に添えて表示するため凡例は不要
  const renderBarChart = (title: string, chartData: ChartItem[]) => {
    const maxValue = Math.max(...chartData.map((item) => item.value));
    return (
      <div
        className={`bar-chart ${
          deviceModeFlg ? "bar-chart-horizontal" : "bar-chart-vertical"
        }`}
        role="list"
        aria-label={`月次集計${title}棒グラフ`}
      >
        {chartData.map((item, index) => {
          const ratio = `${(item.value / maxValue) * 100}%`;
          const color = chartColors[index % chartColors.length];
          const amountText = `${item.amount.toLocaleString()} 円`;
          return (
            <div
              key={item.label}
              className="bar-item"
              role="listitem"
              title={`${item.label}（${amountText}）`}
            >
              <span className="bar-label">{item.label}</span>
              <div className="bar-track" aria-hidden="true">
                <div
                  className="bar-fill"
                  style={
                    deviceModeFlg
                      ? { width: ratio, background: color }
                      : { height: ratio, background: color }
                  }
                />
              </div>
              <span className="bar-amount">{amountText}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderPieChart = (
    target: "income" | "expense",
    title: string,
    chartData: ChartItem[],
  ) => (
    <>
      <button
        type="button"
        className="pie-chart-toggle"
        onClick={() => toggleLegend(target)}
        aria-expanded={activeLegend === target}
        aria-controls={`${target}-legend`}
      >
        <svg
          className="pie-chart"
          viewBox="0 0 200 200"
          aria-label={`月次集計${title}円グラフ`}
        >
          {buildPieSlices(chartData)}
        </svg>
      </button>
      <div
        id={`${target}-legend`}
        className={`pie-legend ${activeLegend === target ? "legend-open" : ""}`}
      >
        {chartData.map((item, index) => (
          <div key={item.label} className="legend-item">
            <span
              className="legend-color"
              style={{
                background: chartColors[index % chartColors.length],
              }}
            />
            <span>{item.label}</span>
            <span>({item.amount.toLocaleString()} 円)</span>
          </div>
        ))}
      </div>
    </>
  );

  // PCの縦棒グラフでは、収入側は項目数分の幅だけを確保し、残りを支出側に割り当てる
  // （収入は項目数が少なく、左右半分ずつだと収入側に余白が生じるため）
  const getChartBlockStyle = (
    target: "income" | "expense",
    chartData: ChartItem[],
  ): React.CSSProperties | undefined => {
    if (deviceModeFlg || chartType !== "bar" || target !== "income") {
      return undefined;
    }
    const barItemWidth = 96;
    const barItemGap = 8;
    const minWidth = 200;
    const width = Math.max(
      chartData.length * (barItemWidth + barItemGap) - barItemGap,
      minWidth,
    );
    return { flex: "0 0 auto", width: `${width}px`, minWidth: 0 };
  };

  const renderChartBlock = (
    target: "income" | "expense",
    title: string,
    chartData: ChartItem[],
  ) => (
    <div className="chart-block" style={getChartBlockStyle(target, chartData)}>
      <div className="chart-subheader">{title}</div>
      {chartData.length === 0 ? (
        <div className="chart-placeholder">{title}のデータがありません。</div>
      ) : chartType === "bar" ? (
        renderBarChart(title, chartData)
      ) : (
        renderPieChart(target, title, chartData)
      )}
    </div>
  );

  const getRowHighlightStyle = (row: any) => ({
    backgroundColor:
      row.categoryLarge === "差分"
        ? "#E9FFA5"
        : row.categoryLarge === "合計"
          ? "#A4C6FF"
          : "inherit",
  });

  const isDifferenceRow = (row: any) => row.categoryLarge === "差分";
  const getTypeDisplay = (row: any) =>
    isDifferenceRow(row) ? "差分" : row.type || "-";
  const getCategoryLargeDisplay = (row: any) =>
    isDifferenceRow(row) ? "-" : row.categoryLarge || "-";
  const getCategorySmallDisplay = (row: any) =>
    isDifferenceRow(row) ? "-" : row.categorySmall || "-";

  // 目標限度額の表示文字列と色のレベルを返す。
  // 未設定・0の場合は割合を算出できないため色を適用しない（level: null）。
  const getTargetLimitDisplay = (row: any) => {
    const target = row.targetAmount;
    if (target == null) return { text: "-", level: null };
    if (Number(target) === 0) return { text: "0", level: null };
    const ratio = (Math.abs(Number(row.amount) || 0) / Number(target)) * 100;
    return {
      text: `${Number(target).toLocaleString()}(${Math.round(ratio)}%)`,
      level: getExpenseRatioLevel(ratio),
    };
  };

  const getTargetLimitClassName = (level: string | null) =>
    level
      ? `target-limit-cell target-limit-colored target-limit-${level}`
      : "target-limit-cell";

  const handleDateInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.showPicker?.();
  };

  const handleDateInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab") return;
    e.preventDefault();
  };

  return (
    <div
      className={`bop020w030-container ${
        deviceModeFlg ? "deviceMode-sp" : "deviceMode-pc"
      }`}
    >
      {isLoading && (
        <div className="bop020w030-loading-overlay">
          <Spinner label="検索中..." />
        </div>
      )}
      <div className="screen-title">月次集計画面</div>
      {/* 検索条件 */}
      <div className="search-layout">
        <details className="search-details" open>
          <summary>検索条件</summary>
          <div className="details-content">
            <form id="searchForm" onSubmit={onSubmit}>
              <div className="form-row">
                <label className="form-label required-label">年月</label>
                <input
                  type="month"
                  value={month}
                  onChange={(e) => onMonthChange(e.target.value)}
                  onClick={handleDateInputClick}
                  onKeyDown={handleDateInputKeyDown}
                />
              </div>
            </form>
          </div>
        </details>
      </div>
      <div className="action-panel">
        <div className="action-group action-group-left">
          <button type="button" className="clear-button" onClick={onBack}>
            戻る
          </button>
          <button
            type="button"
            className="clear-button"
            onClick={onClear}
            disabled={isLoading}
          >
            クリア
          </button>
        </div>
        <div className="action-group action-group-right">
          <button
            form="searchForm"
            type="submit"
            className="search-button"
            disabled={isLoading}
          >
            検索
          </button>
        </div>
      </div>
      {showResults && results.length > 0 && (
        <div className="chart-card">
          <div className="chart-header">
            <span>検索結果グラフ</span>
            <div
              className="chart-type-switch"
              role="group"
              aria-label="グラフの種類"
            >
              {CHART_TYPE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`chart-type-button ${
                    chartType === option.value ? "active" : ""
                  }`}
                  aria-pressed={chartType === option.value}
                  onClick={() => setChartType(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="chart-body">
            {renderChartBlock("income", "収入", incomeChartData)}
            {renderChartBlock("expense", "支出", expenseChartData)}
          </div>
        </div>
      )}
      {/* 検索結果 */}
      {showResults && (
        <details open>
          <summary>検索結果</summary>
          {results.length === 0 ? (
            <div className="no-results-message">検索結果は0件です</div>
          ) : deviceModeFlg ? (
            <div className="result-card-list" role="list">
              {results.map((row) => (
                <article
                  key={row.id}
                  className="result-card"
                  role="listitem"
                  style={getRowHighlightStyle(row)}
                >
                  <div className="result-card-header">
                    <span className="row-type">{getTypeDisplay(row)}</span>
                    {row.categoryLarge !== "差分" && (
                      <button
                        className="detail-button"
                        onClick={() => onDetailClick(row)}
                      >
                        詳細
                      </button>
                    )}
                  </div>
                  <dl className="result-card-grid">
                    <div>
                      <dt>項目（大）</dt>
                      <dd>{getCategoryLargeDisplay(row)}</dd>
                    </div>
                    <div>
                      <dt>項目（小）</dt>
                      <dd>{getCategorySmallDisplay(row)}</dd>
                    </div>
                    <div>
                      <dt>金額（円）</dt>
                      <dd className={Number(row.amount) < 0 ? "minus" : ""}>
                        {Number(row.amount).toLocaleString()}
                      </dd>
                    </div>
                    {(() => {
                      const { text, level } = getTargetLimitDisplay(row);
                      return (
                        <div>
                          <dt>目標限度額（円）</dt>
                          <dd className={getTargetLimitClassName(level)}>
                            {text}
                          </dd>
                        </div>
                      );
                    })()}
                  </dl>
                </article>
              ))}
            </div>
          ) : (
            <div className="table-scroll-container">
              <table className="result-table">
                <thead>
                  <tr>
                    <th
                      className="sortable-header"
                      onClick={() => onSort("type")}
                    >
                      収支
                      {sortKey === "type" && (
                        <span className="sort-indicator">
                          {sortDirection === "asc" ? " ▲" : " ▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => onSort("categoryLarge")}
                    >
                      項目（大）
                      {sortKey === "categoryLarge" && (
                        <span className="sort-indicator">
                          {sortDirection === "asc" ? " ▲" : " ▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => onSort("categorySmall")}
                    >
                      項目（小）
                      {sortKey === "categorySmall" && (
                        <span className="sort-indicator">
                          {sortDirection === "asc" ? " ▲" : " ▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => onSort("amount")}
                    >
                      金額（円）
                      {sortKey === "amount" && (
                        <span className="sort-indicator">
                          {sortDirection === "asc" ? " ▲" : " ▼"}
                        </span>
                      )}
                    </th>
                    <th>目標限度額（円）</th>
                    <th>一覧画面</th>
                  </tr>
                </thead>

                <tbody>
                  {results.map((row) => (
                    <tr key={row.id}>
                      <td style={getRowHighlightStyle(row)}>
                        {getTypeDisplay(row)}
                      </td>
                      <td style={getRowHighlightStyle(row)}>
                        {getCategoryLargeDisplay(row)}
                      </td>
                      <td style={getRowHighlightStyle(row)}>
                        {getCategorySmallDisplay(row)}
                      </td>
                      <td
                        style={{
                          ...getRowHighlightStyle(row),
                          color: row.amount < 0 ? "red" : "inherit",
                          textAlign: "right",
                        }}
                      >
                        {Number(row.amount).toLocaleString()}
                      </td>
                      {(() => {
                        const { text, level } = getTargetLimitDisplay(row);
                        return (
                          <td
                            className={getTargetLimitClassName(level)}
                            // 色を適用する場合は行の強調色より目標限度額の色を優先する
                            style={level ? undefined : getRowHighlightStyle(row)}
                          >
                            {text}
                          </td>
                        );
                      })()}
                      <td
                        style={{
                          ...getRowHighlightStyle(row),
                          textAlign: "center",
                        }}
                      >
                        {row.categoryLarge !== "差分" && (
                          <button
                            className="detail-button"
                            onClick={() => onDetailClick(row)}
                          >
                            詳細
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </details>
      )}
    </div>
  );
};

export default BOP020W030Template;
